import vuexStore from '@/vuex-store';
import { BusinessError } from '@/error';
import { pStore } from '@/pinia';
import { snackbarActionType } from '@/vuex-store/snackbar/actions';
import { openPopup } from '@/utils/popup';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { barogoHQLatLng } from '@/constants/global';
import { strokeConfig } from '@/constants/mapObject';
import { createIcon as createDropIcon } from '@/lib/map-control/marker/icon/dropIcon';
import { createIcon as createStoreIcon } from '@/lib/map-control/marker/icon/storeIcon';
import { createIcon as createWorkerIcon } from '@/lib/map-control/marker/icon/workerIcon';
import { computed } from 'vue';
import { orderStatus } from '@/constants';
import { routeName } from '@/constants/routeName';
import { popupIdData } from '@/constants/popupIdData';
import { getRouterUrl } from '@/utils/url';
/**
 * Composable: 출발도착관제
 */
export const useLocationControl = () => {
    const pLocationControl = pStore.piniaLocationControl;
    const popup = popupIdData[routeName.LOCATION_CONTROL];
    const headerTitle = '출발도착관제';
    const center = barogoHQLatLng;
    const initialZoom = 15;
    const route = useRoute();
    const { orderId, selectedOrder, allocatedWorker, } = storeToRefs(pLocationControl);
    let naver = null;
    let mapInstance = null;
    /** 출발지 */
    let pickupMk = null;
    /** 도착지 */
    let dropMk = null;
    /** 출발 - 도착 경로 */
    let pickupDropPath = null;
    /** 라이더 마커 */
    let workerMk = null;
    /**
     * 네이버 지도 실행
     */
    const initNaverMap = () => {
        naver = window.naver;
        const mapOptions = {
            center: new naver.maps.LatLng(center[0], center[1]),
            zoom: initialZoom,
            mapDataControl: false,
            mapTypeControl: true,
            mapTypeControlOptions: {
                position: naver.maps.Position.TOP_LEFT,
            },
            scaleControl: true,
            scaleControlOptions: {
                position: naver.maps.Position.BOTTOM_RIGHT,
            },
            zoomControl: true,
            zoomControlOptions: {
                style: naver.maps.ZoomControlStyle.SMALL,
                position: naver.maps.Position.RIGHT_TOP,
            },
            logoControlOptions: {
                position: naver.maps.Position.LEFT_BOTTOM,
            },
        };
        mapInstance = new naver.maps.Map('location-control-map', mapOptions);
    };
    /**
     * 마커 초기화
     */
    const clearAllMarker = () => {
        pickupMk?.setMap(null);
        dropMk?.setMap(null);
        pickupDropPath?.setMap(null);
        workerMk?.setMap(null);
    };
    /**
     * 지도 구성요소 출력
     */
    const printMapObject = () => {
        const orderLocation = pLocationControl.getterOrderLocation;
        const workerLocation = pLocationControl.getterWorkerLocation;
        // 외곽 위치 생성
        const boundsData = [
            new naver.maps.LatLng(orderLocation.pickup[0], orderLocation.pickup[1]),
            new naver.maps.LatLng(orderLocation.drop[0], orderLocation.drop[1]),
        ];
        // 출발지
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        pickupMk = new naver.maps.Marker({
            map: mapInstance,
            position: new naver.maps.LatLng(orderLocation.pickup[0], orderLocation.pickup[1]),
            icon: createStoreIcon({
                naver,
                storeId: selectedOrder.value?.storeId,
                type: 'normal',
                state: 'active',
            }),
        });
        // 도착지
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        dropMk = new naver.maps.Marker({
            map: mapInstance,
            position: new naver.maps.LatLng(orderLocation.drop[0], orderLocation.drop[1]),
            icon: createDropIcon({
                naver,
                orderId: orderId.value,
                type: 'normal',
                state: 'active',
            }),
        });
        // 출발 - 도착 경로
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        pickupDropPath = new naver.maps.Polyline({
            path: [
                new naver.maps.LatLng(orderLocation.pickup[0], orderLocation.pickup[1]),
                new naver.maps.LatLng(orderLocation.drop[0], orderLocation.drop[1]),
            ],
            map: mapInstance,
            ...strokeConfig.DROP,
        });
        // 라이더 마커 관련
        // 취소나 완료라면 라이더 마커 숨김 (아닐 때만 라이더 마커 추가)
        const isShowWorkerMk = ![
            orderStatus.CANCELED.code,
            orderStatus.DROP_FINISHED.code,
        ].includes(selectedOrder.value?.statusCode);
        if (selectedOrder.value?.allocatedWorkerCode
            && !allocatedWorker.value?.isOff
            && isShowWorkerMk) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            workerMk = new naver.maps.Marker({
                map: mapInstance,
                position: new naver.maps.LatLng(workerLocation[0], workerLocation[1]),
                icon: createWorkerIcon({
                    iconType: 'name-type',
                    naver,
                    code: allocatedWorker.value.user.code,
                    name: allocatedWorker.value.user.name,
                    attendCode: allocatedWorker.value.user.attendCode,
                    isShareWorker: false,
                    isActive: true,
                }),
            });
            boundsData.push(new naver.maps.LatLng(workerLocation[0], workerLocation[1]));
        }
        // 마커 중심 화면 이동 + 줌
        mapInstance.fitBounds(naver.maps.LatLngBounds.bounds(...boundsData));
    };
    /**
     * 네이버 지도 호출
     */
    const onLoadNaverMap = () => {
        if (window.naver) {
            initNaverMap();
        }
        else {
            document
                .getElementById('naver-map-load')
                // eslint-disable-next-line no-return-assign
                .onload = () => window.naver.maps.onJSContentLoaded = initNaverMap;
        }
    };
    /**
     * 네이버 지도 api 다이나믹 import
     */
    const importNaverMap = async () => {
        if (window.naver) {
            return false;
        }
        const URL = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${process.env.VUE_APP_NAVER_CLIENT_ID}`;
        const script = document.createElement('script');
        if (script) {
            script.setAttribute('src', URL);
            script.id = 'naver-map-load';
            document.head.appendChild(script);
        }
        return true;
    };
    /**
     * 초기화 데이터 조회
     */
    const initData = async () => {
        try {
            await pLocationControl.getOrderAndWorker();
            if (allocatedWorker.value?.isOff) {
                vuexStore.dispatch(snackbarActionType.showError, '배차한 라이더가 오프라인 상태입니다.');
            }
            return true;
        }
        catch (error) {
            if (error instanceof BusinessError) {
                vuexStore.dispatch(snackbarActionType.showError, error.message);
            }
            return false;
        }
    };
    /*
     * query string을 pinia에 주입
     */
    const injectQSToPinia = async () => {
        const _orderId = route.query.orderId;
        if (_orderId) {
            await pLocationControl.setPopupData({
                orderId: _orderId,
            });
            return true;
        }
        return false;
    };
    /**
     * 닫기 버튼 숨기기 여부 체크
     */
    const checkIsHideCloseBtn = computed(() => {
        // 팝업이면 숨기지 않음
        if (window.opener) {
            return false;
        }
        // 본창이면 숨김
        return true;
    });
    /**
     * 지도 출력
     */
    const printMap = async () => {
        if (await initData()) {
            clearAllMarker();
            printMapObject();
        }
    };
    /**
     * 초기화
     */
    const init = async () => {
        importNaverMap();
        onLoadNaverMap();
        // query string 피니아에 추가
        if (!injectQSToPinia()) {
            vuexStore.dispatch(snackbarActionType.showError, '주문아이디는 필수 입니다.');
            return;
        }
        // 초기화 성공시에만 마커 추가
        printMap();
    };
    /**
     * 출발도착관제
     */
    const handleClickOpenPickupDropControlDialog = () => {
        // 팝업 열려 있으면 신규 생성하지 않고 주문 변경 실행
        if (window[popup.id]
            && !window[popup.id].closed) {
            // 메서드 실행
            window[popup.id].changeLocationControlOrder && window[popup.id]
                .changeLocationControlOrder(orderId.value);
            return;
        }
        const url = getRouterUrl({
            routeName: routeName.LOCATION_CONTROL,
            query: {
                orderId: orderId.value,
            },
        }).toString();
        window[popup.id] = openPopup(url, popup);
    };
    /**
     * 출발도착관제 다이얼로그 닫기
     */
    const handleClickNo = () => {
        window.close();
    };
    return {
        init,
        headerTitle,
        checkIsHideCloseBtn,
        handleClickOpenPickupDropControlDialog,
        handleClickNo,
        injectQSToPinia,
        printMap,
    };
};
